import React, { useState, useEffect } from "react";
import InputField from "./InputField";
import SelectField from "./SelectField";
import TextArea from "./TextArea";
import Button from "../button/Button";

const QuestionnaireForm = ({
  initialData = null,
  onSubmit,
  onCancel,
  isLoading = false,
  mode = "create",
}) => {
  const [formData, setFormData] = useState({
    question: "",
    isRequired: false,
    questionType: "text",
    options: [""],
    order: 0,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        question: initialData.question || "",
        isRequired: initialData.isRequired || false,
        questionType: initialData.questionType || "text",
        options: initialData.options?.length > 0 ? initialData.options : [""],
        order: initialData.order || 0,
      });
    }
  }, [initialData]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.question.trim()) {
      newErrors.question = "Question is required";
    } else if (formData.question.length < 5) {
      newErrors.question = "Question must be at least 5 characters long";
    } else if (formData.question.length > 500) {
      newErrors.question = "Question cannot exceed 500 characters";
    }

    if (
      formData.questionType === "multiple_choice" ||
      formData.questionType === "single_choice"
    ) {
      if (formData.options.length < 2) {
        newErrors.options =
          "At least 2 options are required for multiple choice questions";
      } else if (formData.options.some((option) => !option.trim())) {
        newErrors.options = "All options must have content";
      }
    }

    if (formData.order < 0) {
      newErrors.order = "Order must be a positive number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      const submitData = { ...formData };

      // Clean up options array for text questions
      if (formData.questionType === "text") {
        submitData.options = [];
      } else {
        // Remove empty options
        submitData.options = formData.options.filter((option) => option.trim());
      }

      onSubmit(submitData);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...formData.options];
    newOptions[index] = value;
    setFormData((prev) => ({ ...prev, options: newOptions }));

    if (errors.options) {
      setErrors((prev) => ({ ...prev, options: "" }));
    }
  };

  const addOption = () => {
    if (formData.options.length < 10) {
      setFormData((prev) => ({ ...prev, options: [...prev.options, ""] }));
    }
  };

  const removeOption = (index) => {
    if (formData.options.length > 1) {
      const newOptions = formData.options.filter((_, i) => i !== index);
      setFormData((prev) => ({ ...prev, options: newOptions }));
    }
  };

  // Convert to the format expected by SelectField
  const questionTypeOptions = {
    data: [
      { name: "text" },
      { name: "single_choice" },
      { name: "multiple_choice" },
    ],
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Question */}
        <div className="md:col-span-2">
          <TextArea
            id="question"
            label="Question"
            value={formData.question}
            onChange={(e) => handleInputChange("question", e.target.value)}
            placeholder="Enter your question here..."
            error={errors.question}
            touch={true}
            rows={3}
          />
        </div>

        {/* Question Type */}
        <div>
          <SelectField
            id="questionType"
            label="Question Type"
            value={formData.questionType}
            onChange={(e) => handleInputChange("questionType", e.target.value)}
            options={questionTypeOptions}
            error={errors.questionType}
            touch={true}
          />
        </div>

        {/* Order */}
        <div>
          <InputField
            id="order"
            label="Display Order"
            type="number"
            value={formData.order}
            onChange={(e) =>
              handleInputChange("order", parseInt(e.target.value) || 0)
            }
            placeholder="0"
            error={errors.order}
            touch={true}
            min="0"
          />
        </div>

        {/* Required */}
        <div className="md:col-span-2">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.isRequired}
              onChange={(e) =>
                handleInputChange("isRequired", e.target.checked)
              }
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
            />
            <span className="text-sm font-medium text-gray-700">
              This question is required
            </span>
          </label>
        </div>

        {/* Options for multiple choice questions */}
        {(formData.questionType === "multiple_choice" ||
          formData.questionType === "single_choice") && (
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Options
            </label>
            <div className="space-y-3">
              {formData.options.map((option, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <InputField
                    id={`option-${index}`}
                    value={option}
                    onChange={(e) => handleOptionChange(index, e.target.value)}
                    placeholder={`Option ${index + 1}`}
                    mainClass="flex-1"
                  />
                  {formData.options.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeOption(index)}
                      className="p-2 text-red-600 hover:text-red-800 transition-colors"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
              {formData.options.length < 10 && (
                <Button
                  text="Add Option"
                  onClick={addOption}
                  customClass="mt-2"
                />
              )}
            </div>
            {errors.options && (
              <p className="mt-1 text-sm text-red-600">{errors.options}</p>
            )}
          </div>
        )}
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-3 pt-6 border-t">
        <Button
          text="Cancel"
          onClick={onCancel}
          customClass="bg-gray-500 hover:bg-gray-600"
        />
        <Button
          text={mode === "create" ? "Create Template" : "Update Template"}
          submit="submit"
          isLoading={isLoading}
          disabled={isLoading}
        />
      </div>
    </form>
  );
};

export default QuestionnaireForm;
